/**
 * Number/date formatting used across the app. Rates (CTR, CVR, ROAS-as-%) are RATIOS (0.042 = 4.2%)
 * as produced by core `derived()`; pass `{ ratio: false }` when a value is already a percentage.
 */

export type Maybe<T> = T | null | undefined;

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export const DASH = "—";

export function compact(v: Maybe<number>, digits = 1): string {
  if (!isNum(v)) return DASH;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${trim((v / 1e9).toFixed(digits))}B`;
  if (abs >= 1e6) return `${trim((v / 1e6).toFixed(digits))}M`;
  if (abs >= 1e3) return `${trim((v / 1e3).toFixed(digits))}K`;
  return Number.isInteger(v) ? String(v) : trim(v.toFixed(digits));
}

function trim(s: string): string {
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
}

export function integer(v: Maybe<number>): string {
  if (!isNum(v)) return DASH;
  return Math.round(v).toLocaleString("en-US");
}

const SYMBOLS: Record<string, string> = { USD: "$", EUR: "€", GBP: "£", JPY: "¥", KRW: "₩", AUD: "A$", CAD: "C$", INR: "₹" };

export function currencySymbol(currency = "USD"): string {
  return SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
}

/** $1,234.56 — or compact ($1.1K) when `compact` and value ≥ 1000. */
export function money(v: Maybe<number>, currency = "USD", opts: { compact?: boolean; digits?: number } = {}): string {
  if (!isNum(v)) return DASH;
  const sym = currencySymbol(currency);
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (opts.compact && abs >= 1000) return `${sign}${sym}${compact(abs)}`;
  const digits = opts.digits ?? 2;
  return `${sign}${sym}${abs.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/** 0.0421 → "4.2%" (ratio) or 4.21 → "4.2%" with ratio:false. */
export function pct(v: Maybe<number>, opts: { ratio?: boolean; digits?: number } = {}): string {
  if (!isNum(v)) return DASH;
  const value = opts.ratio === false ? v : v * 100;
  const digits = opts.digits ?? (Math.abs(value) >= 100 ? 0 : value === 0 ? 1 : 1);
  return `${trim(value.toFixed(digits)) || "0"}%`;
}

/** Signed delta percentage, e.g. +111% / −4%. Input is a percentage number (already ×100) unless ratio. */
export function deltaPct(v: Maybe<number>, opts: { ratio?: boolean; digits?: number } = {}): string {
  if (!isNum(v)) return DASH;
  const value = opts.ratio ? v * 100 : v;
  const digits = opts.digits ?? (Math.abs(value) >= 10 ? 0 : 1);
  const s = trim(Math.abs(value).toFixed(digits));
  if (s === "0") return "0%";
  return `${value > 0 ? "+" : "−"}${s}%`;
}

export function ratio(v: Maybe<number>, digits = 2): string {
  if (!isNum(v)) return DASH;
  return `${v.toFixed(digits)}x`;
}

export type MetricKind = "money" | "pct" | "count" | "ratio" | "number";

/** Known metric keys → how to render them. */
export const METRIC_KIND: Record<string, MetricKind> = {
  spend: "money",
  revenue: "money",
  cpa: "money",
  cpc: "money",
  cpm: "money",
  cpi: "money",
  cost_per_trial: "money",
  cost_per_install: "money",
  cost_per_purchase: "money",
  costPerTrial: "money",
  costPerInstall: "money",
  costPerPurchase: "money",
  ctr: "pct",
  cvr: "pct",
  hook_rate: "pct",
  hookRate: "pct",
  frequency: "number",
  roas: "ratio",
  impressions: "count",
  clicks: "count",
  conversions: "count",
  installs: "count",
  trials: "count",
  purchases: "count",
  reach: "count",
};

/** Metrics where lower is better (for colouring deltas). */
export const LOWER_IS_BETTER = new Set(["cpa", "cpc", "cpm", "cpi", "cost_per_trial", "cost_per_install", "cost_per_purchase", "costPerTrial", "costPerInstall", "costPerPurchase", "frequency"]);
/** Metrics whose direction is neutral (spend). */
export const NEUTRAL_METRICS = new Set(["spend"]);

export function formatMetric(key: string, v: Maybe<number>, currency = "USD", opts: { compact?: boolean } = {}): string {
  const kind = METRIC_KIND[key] ?? METRIC_KIND[key.toLowerCase()] ?? "number";
  switch (kind) {
    case "money":
      return money(v, currency, { compact: opts.compact });
    case "pct":
      return pct(v);
    case "count":
      return opts.compact ? compact(v) : integer(v);
    case "ratio":
      return ratio(v);
    default:
      return isNum(v) ? compact(v, 2) : DASH;
  }
}

export const METRIC_LABEL: Record<string, string> = {
  spend: "Spend",
  revenue: "Revenue",
  cpa: "CPA",
  cpc: "CPC",
  cpm: "CPM",
  ctr: "CTR",
  cvr: "CVR",
  roas: "ROAS",
  impressions: "Impressions",
  clicks: "Clicks",
  conversions: "Conversions",
  installs: "Installs",
  trials: "Trials",
  purchases: "Purchases",
  frequency: "Frequency",
  reach: "Reach",
  hook_rate: "Hook rate",
};

export function metricLabel(key: string): string {
  return METRIC_LABEL[key] ?? METRIC_LABEL[key.toLowerCase()] ?? humanize(key);
}

/** "problem_aware" → "Problem aware" */
export function humanize(s: Maybe<string>): string {
  if (!s) return "";
  const t = s.replace(/[_.:-]+/g, " ").replace(/\s+/g, " ").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function toDate(v: Maybe<string | number | Date>): Date | null {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "4d ago", "just now", "in 12 days". */
export function relative(v: Maybe<string | number | Date>, now: Date = new Date()): string {
  const d = toDate(v);
  if (!d) return DASH;
  const diff = d.getTime() - now.getTime();
  const abs = Math.abs(diff);
  const future = diff > 0;
  const m = 60_000;
  const h = 60 * m;
  const day = 24 * h;
  let s: string;
  if (abs < 45_000) return future ? "in a moment" : "just now";
  if (abs < h) s = `${Math.round(abs / m)}m`;
  else if (abs < day) s = `${Math.round(abs / h)}h`;
  else if (abs < 30 * day) s = `${Math.round(abs / day)}d`;
  else if (abs < 365 * day) s = `${Math.round(abs / (30 * day))}mo`;
  else s = `${Math.round(abs / (365 * day))}y`;
  return future ? `in ${s}` : `${s} ago`;
}

export function daysUntil(v: Maybe<string | number | Date>, now: Date = new Date()): number | null {
  const d = toDate(v);
  if (!d) return null;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((target - start) / 86_400_000);
}

/** "Oct 17, 2026" */
export function shortDate(v: Maybe<string | number | Date>): string {
  const d = toDate(v);
  if (!d) return DASH;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** "Oct 17, 12:47 PM" */
export function dateTime(v: Maybe<string | number | Date>): string {
  const d = toDate(v);
  if (!d) return DASH;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** ISO date (YYYY-MM-DD) in local time. */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function initials(name: Maybe<string>): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
